import { isSafariBrowser } from './pushPermission';

const IDB_PROBE_DB = '__jh_idb_ready_probe__';
const IDB_PROBE_STORE = 'probe';

/** Created by Firebase Messaging when token persistence succeeds. */
const FIREBASE_MESSAGING_DB_NAME = 'firebase-messaging-database';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const listIndexedDatabases = async () => {
  if (typeof indexedDB === 'undefined') {
    return [];
  }
  try {
    if (typeof indexedDB.databases === 'function') {
      const databases = await indexedDB.databases();
      return Array.isArray(databases) ? databases : [];
    }
  } catch (_) {
    // databases() unsupported or blocked (some WebViews).
  }
  return [];
};

/**
 * True when Firebase Messaging has opened its IndexedDB store (token sync completed at least once).
 */
export const hasFirebaseMessagingDatabase = async () => {
  const databases = await listIndexedDatabases();
  return databases.some((entry) => entry?.name === FIREBASE_MESSAGING_DB_NAME);
};

/**
 * Poll until firebase-messaging-database appears or timeout.
 * Used after a failed getToken when other Firebase IDB stores already exist.
 */
export const waitForFirebaseMessagingDatabase = async ({
  timeoutMs = 10000,
  pollMs = 200,
} = {}) => {
  if (typeof indexedDB === 'undefined') {
    return false;
  }

  if (await hasFirebaseMessagingDatabase()) {
    return true;
  }

  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    await sleep(pollMs);
    if (await hasFirebaseMessagingDatabase()) {
      return true;
    }
  }

  return false;
};

/**
 * Safari can expose `indexedDB` before the first open succeeds on a cold visit.
 * Probe with a throwaway database until open succeeds or we time out.
 */
export const waitForIndexedDBReady = async ({
  timeoutMs = 8000,
  pollMs = 150,
} = {}) => {
  if (typeof indexedDB === 'undefined') {
    return false;
  }

  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const ready = await new Promise((resolve) => {
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      try {
        const request = indexedDB.open(IDB_PROBE_DB, 1);
        request.onerror = () => finish(false);
        request.onblocked = () => finish(false);
        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains(IDB_PROBE_STORE)) {
            db.createObjectStore(IDB_PROBE_STORE);
          }
        };
        request.onsuccess = (event) => {
          try {
            event.target.result.close();
          } catch (_) {
            // ignore
          }
          try {
            indexedDB.deleteDatabase(IDB_PROBE_DB);
          } catch (_) {
            // ignore
          }
          finish(true);
        };
      } catch (_) {
        finish(false);
      }
    });

    if (ready) {
      return true;
    }

    await sleep(pollMs);
  }

  return false;
};

/**
 * Short yield after SW registration — cold tabs (especially Safari) often need a beat
 * before Firebase Messaging can write firebase-messaging-database.
 */
export const waitForFcmEnvironmentReady = async () => {
  if (typeof window === 'undefined') {
    return;
  }

  await waitForIndexedDBReady({
    timeoutMs: isSafariBrowser() ? 10000 : 8000,
    pollMs: 150,
  });

  await new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
  await sleep(isSafariBrowser() ? 100 : 50);
};
